import { useState, useMemo, useRef, useEffect } from "react";
import { Box, Typography, IconButton, Collapse } from "@mui/material";
import { ChatBubbleOutline } from "@mui/icons-material";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import { ChatMessage, CompletionsRequest } from "../../Models/chatUI";
import { useCompletions } from "../../Query/useChats";
import ChatMarkdown from "../Common/ChatMarkdown";
import CopyButton from "../Common/CopyButton";
import {
  AnswerDetail,
  CategoryDetail,
  Questions,
} from "../../Models/evaluation";
import { useTranslation } from "react-i18next";
import { getGradeText } from "../../Helpers/gradeHelpers";
import colors from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ComoInput } from "../Common/ComoInput";

interface SectionChatProps {
  section: CategoryDetail;
  questions?: Questions[];
  answers?: AnswerDetail[];
}

export function SectionChat({ section, questions, answers }: SectionChatProps) {
  const { t } = useTranslation("singleCompanyComponent");
  const { colors: workspace } = useWorkspaceColors();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const completionsMutation = useCompletions();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isThinking]);

  const systemPrompt = useMemo(() => {
    const parts: string[] = [];

    parts.push(`Category: ${section.question_category_name}`);

    if (section.summary) {
      parts.push(`Summary: ${section.summary}`);
    }

    if (section.note) {
      parts.push(`Note: ${section.note}`);
    }

    if (questions && questions.length > 0) {
      parts.push(`Questions: ${questions.map((q) => q.question).join(", ")}`);
    }

    if (answers && answers.length > 0) {
      parts.push(`Answers: ${answers.map((a) => a.answer).join(", ")}`);
    }

    parts.push(`Category Grade: ${section.category_grade}%`);
    parts.push(`Grade Description: ${t(getGradeText(section.category_grade))}`);

    return parts.join("\n\n");
  }, [section, questions, answers, t]);

  const handleSend = () => {
    if (!message.trim() || isThinking) return;

    const userMessage: ChatMessage = {
      role: "user",
      content: message,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsChatExpanded(true);
    setMessage("");
    setIsThinking(true);

    const history: Array<{
      role: "system" | "user" | "assistant";
      content: string;
    }> = [
      { role: "system", content: systemPrompt },
      ...messages.map((msg) => ({
        role: msg.role as "user" | "assistant",
        content: msg.content,
      })),
      { role: "user", content: message },
    ];

    const request: CompletionsRequest = {
      messages: history,
      model: "comma-soft/gemma4-31b-instant",
      temperature: 0.1,
      top_p: 1.0,
      max_tokens: 1000,
    };

    completionsMutation.mutate(request, {
      onSuccess: (response) => {
        const assistantMessage: ChatMessage = {
          role: "assistant",
          content: response.response.replace(/\$\\rightarrow\$/g, "→"),
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
        setIsThinking(false);
      },
      onError: () => {
        const errorMessage: ChatMessage = {
          role: "assistant",
          content: t("sectionChat.chatError"),
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        setIsThinking(false);
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        bgcolor: colors.blue50,
        borderRadius: 2,
        p: 1,
        pl: 1.5,
        border: `1px solid ${colors.blue200}`,
        borderLeft: `4px solid ${workspace.primary_dark}`,
      }}
    >
      <Collapse in={isChatExpanded}>
        <Box
          ref={messagesEndRef}
          sx={{
            mb: 2,
            maxHeight: 350,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            scrollBehavior: "smooth",
          }}
        >
          {messages.map((msg, index) => (
            <Box
              key={index}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: msg.role === "user" ? colors.gray100 : "white",
                ml: msg.role === "user" ? 6 : 0,
                mr: msg.role === "assistant" ? 6 : 0,
                border: `1px solid ${
                  msg.role === "user" ? colors.gray200 : colors.blue200
                }`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color:
                      msg.role === "user"
                        ? colors.gray600
                        : workspace.primary_dark,
                  }}
                >
                  {msg.role === "user"
                    ? t("sectionChat.userLabel")
                    : t("sectionChat.assistantLabel")}
                </Typography>
                {msg.role === "assistant" && (
                  <CopyButton
                    content={msg.content}
                    size="medium"
                    color={workspace.primary_dark}
                  />
                )}
              </Box>
              <ChatMarkdown content={msg.content} />
            </Box>
          ))}

          {isThinking && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "white",
                mr: 6,
                border: `1px solid ${colors.blue200}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: workspace.primary_dark,
                  display: "block",
                  mb: 0.5,
                }}
              >
                {t("sectionChat.assistantLabel")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  "& span": {
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: workspace.primary_dark,
                    animation: "pulse 1.4s infinite ease-in-out",
                  },
                  "& span:nth-of-type(1)": {
                    animationDelay: "0s",
                  },
                  "& span:nth-of-type(2)": {
                    animationDelay: "0.2s",
                  },
                  "& span:nth-of-type(3)": {
                    animationDelay: "0.4s",
                  },
                  "@keyframes pulse": {
                    "0%, 80%, 100%": {
                      opacity: 0.3,
                      transform: "scale(0.8)",
                    },
                    "40%": {
                      opacity: 1,
                      transform: "scale(1)",
                    },
                  },
                }}
              >
                <span />
                <span />
                <span />
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: workspace.primary_dark,
            textTransform: "uppercase",
            textWrap: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 0.7,
          }}
        >
          <ChatBubbleOutline sx={{ fontSize: "1rem" }} />
          {t("sectionChat.askAiLabel")}
        </Typography>
        <ComoInput
          placeholder={t("sectionChat.placeholder")}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isThinking}
        />
        <IconButton
          onClick={handleSend}
          disabled={!message.trim() || isThinking}
          sx={{
            p: 0,
            color: workspace.primary_dark,
            cursor: "pointer",
            "&:disabled": { color: colors.gray500 },
          }}
        >
          <PublishRoundedIcon sx={{ fontSize: "2rem" }} />
        </IconButton>
      </Box>
    </Box>
  );
}
